import React, { useEffect } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import Image from '@atlaskit/image';
import { useIsReducedMotion } from '@atlaskit/motion';
import { fg } from '@atlaskit/platform-feature-flags';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import GeneratingAnimation from '../../../ai-answer-dialog/ai-answer-loading/generating-animation';
import {
	ReducedMotionRovoGeneratingAnimation,
	RovoGeneratingAnimation,
} from '../../../ai-answer-dialog/ai-answer-loading/rovo-generating-animation';
import { onAIAnswerLoadingDialogShown } from '../../../ai-answer-dialog/analytics';
import { useAnalytics } from '../../../common/controller/analytics/use-analytics';
import { QueryIntent } from '../../../constants';
import { messages } from '../../smart-answer-card/messages';

import RovoIcon from './assets/Rovo.svg';

const styles = cssMap({
	container: {
		paddingTop: token('space.200'),
		paddingLeft: token('space.200'),
		height: '198px',
	},
	containerNewSAINLoading: {
		paddingRight: token('space.200'),
		paddingLeft: token('space.200'),
		paddingTop: token('space.200'),
		paddingBottom: token('space.200'),
		height: '198px',
	},
	inline: {
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		height: '40px',
	},
	jiraNlToJqlCardHeightOverride: {
		height: '382px',
	},
	confluenceResultCardHeightOverride: {
		height: '334px',
	},
	explicitQuestion: {
		height: '350px',
	},
});

type SmartAnswerCardBodyGeneratingProps = {
	heightOverride?: 'jiraNlToJqlCard' | 'confluenceResultCard';
	queryIntent?: QueryIntent;
};
export function SmartAnswerCardBodyGenerating({
	heightOverride,
	queryIntent,
}: SmartAnswerCardBodyGeneratingProps): React.JSX.Element {
	const { commonAttributes, queryReasoning } = useAIAnswerContext();
	const [fireAnalyticsEvent] = useAnalytics();
	const isReducedMotion = useIsReducedMotion();

	const showNewSAINLoading = fg('kd_sain_rovo_loading');

	// TODO: Temporary function for TEAMEU - normalize reasoning strings by lowercasing first letter and removing trailing periods
	const normalizeReasoningString = (str: string): string => {
		if (!str) {
			return str;
		}
		let result = `${str.charAt(0).toLowerCase()}${str.slice(1)}`;
		// Remove trailing period if it exists
		if (result.endsWith('.')) {
			result = result.slice(0, -1);
		}
		return result;
	};

	useEffect(() => {
		fireAnalyticsEvent(onAIAnswerLoadingDialogShown(commonAttributes));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (showNewSAINLoading) {
		return (
			<Stack
				xcss={cx(
					styles.containerNewSAINLoading,
					heightOverride === 'jiraNlToJqlCard' && styles.jiraNlToJqlCardHeightOverride,
					heightOverride === 'confluenceResultCard' && styles.confluenceResultCardHeightOverride,
					queryIntent === QueryIntent.EXPLICIT_QUESTION && styles.explicitQuestion,
				)}
				space="space.200"
			>
				<Inline space={'space.100'} xcss={styles.inline} alignBlock="center">
					<Image src={RovoIcon} alt="" />
					<Inline>
						<Text color={'color.text.subtle'}>
							{queryReasoning ? (
								<FormattedMessage
									{...messages.smart_answer_card_rovo_generating}
									values={{ queryReasoning: normalizeReasoningString(queryReasoning) }}
								/>
							) : (
								<FormattedMessage {...messages.smart_answer_card_rovo_is_thinking} />
							)}
						</Text>
						{isReducedMotion ? (
							<ReducedMotionRovoGeneratingAnimation />
						) : (
							<RovoGeneratingAnimation />
						)}
					</Inline>
				</Inline>
				<Skeleton width="100%" height="16px" color={token('color.skeleton.subtle')} />
				<Skeleton width="100%" height="16px" color={token('color.skeleton.subtle')} />
				<Skeleton width="90%" height="16px" color={token('color.skeleton.subtle')} />
			</Stack>
		);
	}

	return (
		<Inline
			xcss={cx(
				styles.container,
				heightOverride === 'jiraNlToJqlCard' && styles.jiraNlToJqlCardHeightOverride,
				queryIntent === QueryIntent.EXPLICIT_QUESTION && styles.explicitQuestion,
			)}
			space="space.050"
		>
			<GeneratingAnimation />
			<Text color="color.text.subtlest">
				{queryReasoning ? (
					queryReasoning
				) : (
					<FormattedMessage {...messages.smart_answer_card_generating} />
				)}
			</Text>
		</Inline>
	);
}
